import { ChevronDownRegular, ChevronRightRegular, HourglassHalfRegular } from '@fluentui/react-icons';
import { DirectionalHint, TooltipHost } from '@fluentui/react';
import React, { useState } from 'react';

import styles from './CosmicCostTree.less';

interface CosmicCostAndCoreNode {
    id: string;
    name: string;
    level: number;
    children?: CosmicCostAndCoreNode[];
    hasChildren: string;
    parent?: string;
    cost: number;
    appCores: number;
    fragmentationCores: number;
    emptyAppCores: number;
    totalCores: number;
    pricePerCore: number;
}

interface WorkloadFilterProps {
    data: CosmicCostAndCoreNode[];
    onSelectionChange: (selectedIds: string[], deselect?: boolean) => Promise<void>;
    totalCost?: number;
    totalAppCores?: number;
    totalFragmentationCores?: number;
    totalEmptyAppCores?: number;
    totalCores?: number;
    totalPricePerCore?: number;
}

const getLevelName = (level: number): string => {
    switch (level) {
        case 0:
            return 'Workload';
        case 1:
            return 'Category Mapping';
        case 2:
            return 'OrganizationName';
        case 3:
            return 'ServiceGroupName';
        case 4:
            return 'MeterCategoryName';
        default:
            return 'Unknown Level';
    }
};

function formatNumber(value: number): string {
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const CosmicCostAndCoreTree: React.FC<WorkloadFilterProps> = ({
                                                                  data,
                                                                  onSelectionChange,
                                                                  totalCost = 0,
                                                                  totalAppCores = 0,
                                                                  totalFragmentationCores = 0,
                                                                  totalEmptyAppCores = 0,
                                                                  totalCores = 0,
                                                                  totalPricePerCore = 0
                                                              }) => {
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const [selectedNodes] = useState<Set<string>>(new Set());
    const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());

    const toggleNode = async (node: CosmicCostAndCoreNode, e: React.MouseEvent) => {
        e.stopPropagation();
        const nodeId = node.id;
        if (node.hasChildren=="false") {
            return;
        }
        if (!expandedNodes.has(nodeId)) {
            setLoadingNodes(prev => new Set(prev).add(nodeId));
            await onSelectionChange([nodeId]);
            setLoadingNodes(prev => {
                const newLoading = new Set(prev);
                newLoading.delete(nodeId);
                return newLoading;
            });
        } else {
            onSelectionChange([nodeId], true); // Deselect if already expanded
        }
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        } else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    };

    const renderNode = (node: CosmicCostAndCoreNode, level = 0) => {
        const hasChildren = node.hasChildren;
        const isExpanded = expandedNodes.has(node.id);
        const isSelected = selectedNodes.has(node.id);
        const isLoading = loadingNodes.has(node.id);

        return (
            <div key={node.id}>
                <div
                    className={styles.node}
                    style={{ paddingLeft: `${level * 20}px` }}
                >
                    {hasChildren == "true" ? (
                        <>
                            <HourglassHalfRegular
                                className={styles.expandIcon}
                                onClick={(e) => toggleNode(node, e)}
                                style={{ display: isLoading ? 'inline-block' : 'none' }}
                            />
                            <ChevronDownRegular
                                className={styles.expandIcon}
                                onClick={(e) => toggleNode(node, e)}
                                style={{ display: isExpanded && !isLoading ? 'inline-block' : 'none' }}
                            />
                            <ChevronRightRegular
                                className={styles.expandIcon}
                                onClick={(e) => toggleNode(node, e)}
                                style={{ display: !isExpanded && !isLoading ? 'inline-block' : 'none' }}
                            />
                        </>
                    ) : (
                        <span style={{ width: 20 }} /> // Placeholder for alignment
                    )}
                    <TooltipHost
                        content={`${getLevelName(level)}`}
                        directionalHint={DirectionalHint.leftCenter}
                    >
                        <div
                            className={`${styles.nodeContent} ${isSelected ? styles.selected : ''}`}
                            onClick={(e) => toggleNode(node, e)}
                        >
                            <span className={styles.nodeName}>
                                {node.name}
                            </span>
                            {node.cost !== undefined && (
                                <span className={styles.nodeCost}>
                                    ${formatNumber(node.cost)}
                                </span>
                            )}
                            {node.appCores !== undefined && (
                                <span className={styles.nodeCost}>
                                    {formatNumber(node.appCores)}
                                </span>
                            )}
                            {node.fragmentationCores !== undefined && (
                                <span className={styles.nodeCost}>
                                    {formatNumber(node.fragmentationCores)}
                                </span>
                            )}
                            {node.emptyAppCores !== undefined && (
                                <span className={styles.nodeCost}>
                                    {formatNumber(node.emptyAppCores)}
                                </span>
                            )}
                            {node.totalCores !== undefined && (
                                <span className={styles.nodeCost}>
                                    {formatNumber(node.totalCores)}
                                </span>
                            )}
                            {node.pricePerCore !== undefined && (
                                <span className={styles.nodeCost}>
                                    ${formatNumber(node.pricePerCore)}
                                </span>
                            )}
                        </div>
                    </TooltipHost>
                </div>
                {hasChildren == "true" && isExpanded && node.children && (
                    <div className={styles.children}>
                        {node.children.map(child => renderNode(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    const renderTotalRow = () => {
        return (
            <div className={styles.totalRow}>
                <span className={styles.totalName}>Total</span>
                <span className={styles.totalValue}>${formatNumber(totalCost)}</span>
                <span className={styles.totalValue}>{formatNumber(totalAppCores)}</span>
                <span className={styles.totalValue}>{formatNumber(totalFragmentationCores)}</span>
                <span className={styles.totalValue}>{formatNumber(totalEmptyAppCores)}</span>
                <span className={styles.totalValue}>{formatNumber(totalCores)}</span>
                <span className={styles.totalValue}>${formatNumber(totalPricePerCore)}</span>
            </div>
        );
    };

    return (
        <div className={styles.cosmicCostTree}>
            <div className={styles.header}>
                <span className={styles.headerName}>Workload</span>
                <span className={styles.headerCost}>Cost</span>
                <span className={styles.headerCost}>Avg App Cores</span>
                <span className={styles.headerCost}>Avg Fragmentation Cores</span>
                <span className={styles.headerCost}>Avg Empty App Cores</span>
                <span className={styles.headerCost}>Total Cores</span>
                <span className={styles.headerCost}>Price/Core</span>
            </div>
            <div className={styles.treeContainer}>
                {data.map(node => renderNode(node))}
                {renderTotalRow()}
            </div>
        </div>
    );
};

export default CosmicCostAndCoreTree;